<template>
  <div :id="cardId" class="flex flex-col gap-4">
    <div
      class="flex flex-col flex-1 gap-4"
      :class="{ 'cursor-pointer': props.isInfoClickable }"
      @click="$emit('comment-info-click', props.commentId)"
    >
      <div class="flex flex-row items-center gap-2">
        <review-avatar :username="reviewUsername" />
        <div class="flex flex-col gap-1 flex-1">
          <p class="text-sm pl-0.5">{{ displayCommenterName }}</p>
          <div class="flex flex-row items-center gap-2">
            <svg-icon v-for="num in props.reviewRating" :key="num" name="star-filled" class="text-yellow-50 size-4" />
            <svg-icon v-for="num in MAX_RATING - props.reviewRating" :key="num" name="star-filled" class="text-gray-30 size-4" />
            <p class="text-sm text-gray-80">{{ formatDateWithI18n(props.reviewCreateTime, 'MMMDYYYY') }}</p>
          </div>
        </div>
        <etg-button v-if="props.commentCount" type="text" size="mini" class="active self-start -mr-3" @click="$emit('comment-count-click')">
          {{ $t('booking_detail.comment.all', { review_count: formatNumberWithCommas(props.commentCount) }, props.commentCount) }}
        </etg-button>
      </div>
      <p v-if="props.reviewContent" class="whitespace-pre-wrap" :class="props.reviewContentClass">{{ props.reviewContent }}</p>
      <review-card-tags v-if="props.reviewTags.length" :tags="props.reviewTags" :one-line="props.oneLineTags" />
    </div>
    <div class="review-card-footer flex justify-between items-center gap-3">
      <div v-if="externalSource" class="flex items-center gap-1 select-none">
        <n-tooltip
          placement="top-start"
          :show-arrow="false"
          :trigger="appStore.isMobileScreen ? 'click' : 'hover'"
          :content-style="tooltipContentStyle"
        >
          <template #trigger>
            <etg-button type="icon" size="mini" icon="alert" icon-color="gray-80" />
          </template>
          <p class="max-w-72 whitespace-pre-line text-sm text-black-50">
            {{ $t('review.platform.hint', { platform: externalSource }) }}
          </p>
        </n-tooltip>
        <span class="text-sm text-gray-80">{{ $t('review.platform', { platform: externalSource }) }}</span>
      </div>
      <etg-button
        v-if="props.isUsefulBtnShown && props.commentId"
        :icon="likedIconName"
        :icon-color="likedIconColor"
        size="mini"
        class="!py-0 -mr-3 ml-auto"
        @click="onUsefulClick"
      >
        {{ $t('comment.useful.count', { count: formatNumberWithCommas(likesCount) }) }}
      </etg-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { NTooltip } from 'naive-ui'
import type { HTMLAttributes } from 'vue'
import type { ReviewReviewTag } from '@/types/api'
import { ReviewSource } from '@/types/review'

import ReviewAvatar from '@/components/review/review-avatar.vue'
import ReviewCardTags from '@/components/review/review-card-tags.vue'

import { useAuthStore } from '@/store/auth'
import { useAppStore } from '@/store/app'
import { REVIEW_CARD_ID_PREFIX } from '@/constants/commons'

const SOURCE_PLATFORM_MAP: Partial<Record<ReviewSource, string>> = {
  [ReviewSource.FUNNOW]: 'FunNow',
  [ReviewSource.NICEDAY]: 'Niceday',
}

const tooltipContentStyle = {
  padding: '20px 16px',
  borderRadius: '4px',
  boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.2)',
  backgroundColor: '#fff',
}

interface Props {
  reviewUsername: string
  reviewRating: number
  reviewContent: string
  reviewTags: ReviewReviewTag[]
  reviewCreateTime: string
  isInfoClickable?: boolean
  commentId?: number
  commentCount?: number
  isUsefulBtnShown?: boolean
  isUseful?: boolean
  usefulCount?: number
  reviewContentClass?: HTMLAttributes['class']
  placement?: 'product' | 'order' | 'viewer'
  oneLineTags?: boolean
  reviewSource?: ReviewSource
}

interface Emits {
  (event: 'comment-info-click', commentId?: number): void
  (event: 'comment-count-click'): void
}

const MAX_RATING = 5

const props = withDefaults(defineProps<Props>(), { isUsefulBtnShown: false, isUseful: false, usefulCount: 0, oneLineTags: false })
const { isUseful, usefulCount } = toRefs(props)

defineEmits<Emits>()

const authStore = useAuthStore()
const appStore = useAppStore()

const commenterName = computed(() => props.reviewUsername)
const { displayCommenterName } = useComment(commenterName)

const { formatDateWithI18n } = useI18nDateString()
const { likesCount, likedIconName, likedIconColor, fetchLikeReviewApi } = useLikeReview({ isUseful, usefulCount })

const cardId = computed(() => {
  return props.placement ? [props.placement, REVIEW_CARD_ID_PREFIX, props.commentId].join('-') : undefined
})

const externalSource = computed(() => SOURCE_PLATFORM_MAP[props.reviewSource as ReviewSource] ?? null)

function onUsefulClick() {
  if (!authStore.isLogin) {
    authStore.addToLoginPendingActions(onUsefulClick)
    authStore.openLoginModal()

    return
  }

  fetchLikeReviewApi(props.commentId)
}
</script>
